import { ReactNode, useEffect } from 'react';

interface Props {
  open: boolean;
  onClose: () => void;
  side?: 'left' | 'right';
  width?: string;
  ariaLabel: string;
  children: ReactNode;
}

export default function Drawer({ open, onClose, side = 'right', width = '420px', ariaLabel, children }: Props) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  const borderSide = side === 'right' ? 'border-l-2' : 'border-r-2';
  const shadowOffset = side === 'right' ? '-8px 0 24px -6px rgba(34,34,34,0.22), -2px 0 6px -1px rgba(34,34,34,0.10)' : '8px 0 24px -6px rgba(34,34,34,0.22), 2px 0 6px -1px rgba(34,34,34,0.10)';

  return (
    <>
      <div
        onClick={onClose}
        className={`fixed inset-0 bg-ink/75 backdrop-blur-[6px] z-[1000] transition-opacity duration-300 ${open ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        aria-hidden="true"
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        style={{
          width,
          maxWidth: '100vw',
          boxShadow: open ? shadowOffset : 'none',
          transformOrigin: side === 'right' ? 'right center' : 'left center',
        }}
        className={`fixed top-0 bottom-0 ${side === 'right' ? 'right-0' : 'left-0'} z-[1001] bg-paper ${borderSide} border-ink flex flex-col transition-transform duration-300 ease-[cubic-bezier(0.34,1.4,0.5,1)] ${
          open
            ? 'translate-x-0 rotate-0'
            : side === 'right' ? 'translate-x-[125%] rotate-[7deg]' : '-translate-x-[125%] -rotate-[7deg]'
        }`}
      >
        {children}
      </aside>
    </>
  );
}
